/**
 * Componente HorizontalScroll
 * Sección con scroll horizontal controlado por scroll vertical
 * Efecto premium tipo Apple/Heinz
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Sección con scroll horizontal vinculado al scroll vertical
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Elementos a mostrar horizontalmente
 * @param {string} props.className - Clases CSS del contenedor
 * @param {string} props.backgroundColor - Color de fondo
 * @param {number} props.speed - Velocidad del scroll (1 = normal)
 * @returns {JSX.Element} Sección con scroll horizontal
 */
const HorizontalScroll = ({
  children,
  className = '',
  backgroundColor = '#FAFAF2',
  speed = 1,
}) => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const container = containerRef.current;

    if (!section || !container) return;

    // Calcular el ancho total del contenido
    const scrollWidth = container.scrollWidth;
    const viewportWidth = window.innerWidth;
    const distance = scrollWidth - viewportWidth;

    // Crear animación de scroll horizontal
    gsap.to(container, {
      x: -distance,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => `+=${distance * speed}`,
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [speed, children]);

  return (
    <section
      ref={sectionRef}
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundColor }}
    >
      <div
        ref={containerRef}
        className="flex items-center h-screen will-change-transform"
        style={{ width: 'max-content' }}
      >
        {children}
      </div>
    </section>
  );
};

HorizontalScroll.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  backgroundColor: PropTypes.string,
  speed: PropTypes.number,
};

/**
 * Elemento individual para el scroll horizontal
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Contenido del panel
 * @param {string} props.className - Clases CSS adicionales
 * @param {string} props.width - Ancho del panel (ej: '100vw', '50vw')
 * @returns {JSX.Element} Panel horizontal
 */
const HorizontalPanel = ({
  children,
  className = '',
  width = '100vw',
}) => {
  return (
    <div
      className={`flex-shrink-0 h-full flex items-center justify-center p-8 md:p-16 ${className}`}
      style={{ width }}
    >
      {children}
    </div>
  );
};

HorizontalPanel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  width: PropTypes.string,
};

export { HorizontalPanel };
export default HorizontalScroll;
